import { createRef, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import { useAuth } from '../../../context/AuthContext';
import { createTournament } from '../../../services/tournamentService';
import { hasIndividualAccess } from '../../../utils/userRoles';
import { focusFirstError, normalizeFormError, TOURNAMENT_FIELD_ORDER, validateTournamentForm } from '../../../utils/formUx';
import './tournamentFormUx.css';

const BALL_OPTIONS = ['TENNIS', 'LEATHER', 'OTHER'];
const PITCH_OPTIONS = ['ROUGH', 'CEMENT', 'TURF', 'ASTROTURF', 'MATTING'];
const MATCH_OPTIONS = ['LIMITED_OVERS', 'BOX_CRICKET', 'TEST_MATCH'];

const CITY_OPTIONS = ['Bengaluru (Bangalore)', 'Mumbai', 'Delhi', 'Hyderabad', 'Chennai', 'Pune'];
const GROUND_OPTIONS = ['City Cricket Ground', 'Municipal Stadium', 'Sports Complex Turf', 'Community Cricket Ground'];
const ChipGroup = ({ options, value, onChange }) => (
  <div className="d-flex flex-wrap gap-2">
    {options.map((option) => (
      <button
        key={option}
        type="button"
        className={`btn btn-sm ${value === option ? 'btn-primary' : 'btn-outline-secondary'}`}
        onClick={() => onChange(option)}
      >
        {option.replaceAll('_', ' ')}
      </button>
    ))}
  </div>
);

const FieldError = ({ id, message }) => message ? <div id={id} className="tournament-field-error" role="alert">{message}</div> : null;

const IndividualTournamentCreatePage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState('');
  const [bannerPreview, setBannerPreview] = useState('');
  const [form, setForm] = useState({
    name: '',
    tournamentBanner: '',
    tournamentLogo: '',
    city: '',
    ground: '',
    organizerName: '',
    organizerNumber: '',
    tournamentStartDate: '',
    tournamentEndDate: '',
    registrationEndDate: '',
    ballType: 'TENNIS',
    pitchType: 'TURF',
    matchType: 'LIMITED_OVERS',
    allowPublicJoin: true,
    maxPlayersAllowed: '',
  });

  const hasAccess = useMemo(() => hasIndividualAccess(user), [user]);
  const fieldRefs = useMemo(() => Object.fromEntries(TOURNAMENT_FIELD_ORDER.map((key) => [key, createRef()])), []);

  const setField = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    if (errors[key]) setErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  const moveTo = (field) => {
    const node = fieldRefs[field]?.current;
    node?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    if (node?.matches?.('input, textarea')) node.focus({ preventScroll: true });
  };

  if (!token) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/dashboard/individual/tournaments" replace />;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const nextErrors = validateTournamentForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setStatus(`${Object.keys(nextErrors).length} field${Object.keys(nextErrors).length === 1 ? '' : 's'} need your attention.`);
      focusFirstError(nextErrors, fieldRefs);
      return;
    }
    setSubmitting(true);
    setStatus('');
    try {
      const response = await createTournament({
        ...form,
        ownerType: 'individual',
        allowPublicJoin: true,
        scopeType: 'individual',
        entityType: 'individual',
        maxPlayersAllowed: form.maxPlayersAllowed ? Number(form.maxPlayersAllowed) : null,
      }, token);
      const createdTournament = response?.tournament || null;
      const tournamentId = createdTournament?.id || createdTournament?._id;
      const createdOwnerType = String(createdTournament?.ownerType || '').toLowerCase();
      const joinUrlFromResponse = response?.joinUrl || response?.link || response?.tournamentJoinLink?.joinUrl;
      if (joinUrlFromResponse) {
        const joinUrl = /^https?:\/\//i.test(joinUrlFromResponse)
          ? joinUrlFromResponse
          : `${window.location.origin}${joinUrlFromResponse.startsWith('/') ? '' : '/'}${joinUrlFromResponse}`;
        await navigator.clipboard.writeText(joinUrl).catch(() => {});
        setStatus(`Tournament created. Join link copied: ${joinUrl}`);
      } else {
        setStatus('Tournament created successfully.');
      }
      if (createdOwnerType === 'individual' && tournamentId) {
        const adminRoute = `/individual-tournament/${tournamentId}/admin`;
        navigate(adminRoute);
        return;
      }
    } catch (error) {
      const normalized = normalizeFormError(error);
      setStatus(normalized.message);
      if (normalized.field) {
        const serverErrors = { ...errors, [normalized.field]: normalized.message };
        setErrors(serverErrors);
        focusFirstError(serverErrors, fieldRefs);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <div className="container py-4" style={{ maxWidth: 920 }}>
        <h1 className="h3 mb-4">Add A Tournament</h1>
        <form className="d-grid gap-4" onSubmit={onSubmit} noValidate>
          <div className="card p-3">
            <h2 className="h6">Basic Info</h2>
            <div className="row g-3">
              {[
                ['name', 'Tournament Name *', 'text', 'organizerName'],
                ['organizerName', 'Organizer Name *', 'text', 'city'],
                ['city', 'City *', 'text', 'ground'],
                ['ground', 'Ground *', 'text', 'organizerNumber'],
                ['organizerNumber', 'Organizer Number *', 'tel', 'tournamentStartDate'],
              ].map(([key, label, type, next]) => <div className="col-md-6" key={key}><label className="form-label" htmlFor={`tournament-${key}`}>{label}</label><input id={`tournament-${key}`} ref={fieldRefs[key]} type={type} inputMode={type === 'tel' ? 'tel' : undefined} autoComplete={key === 'organizerName' ? 'name' : key === 'organizerNumber' ? 'tel' : key === 'city' ? 'address-level2' : 'off'} list={key === 'city' ? 'city-options' : key === 'ground' ? 'ground-options' : undefined} aria-invalid={Boolean(errors[key])} aria-describedby={`tournament-${key}-error`} className={`form-control ${errors[key] ? 'is-invalid' : ''}`} value={form[key]} onChange={(e) => setField(key, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); moveTo(next); } }} /><FieldError id={`tournament-${key}-error`} message={errors[key]} /></div>)}
            </div>
            <datalist id="city-options">{CITY_OPTIONS.map((item) => <option value={item} key={item} />)}</datalist>
            <datalist id="ground-options">{GROUND_OPTIONS.map((item) => <option value={item} key={item} />)}</datalist>
          </div>

          <div className="card p-3">
            <h2 className="h6">Dates</h2>
            <div className="row g-3">
              {[
                ['tournamentStartDate', 'Start Date *', '', '', 'tournamentEndDate'],
                ['tournamentEndDate', 'End Date *', form.tournamentStartDate, '', 'registrationEndDate'],
                ['registrationEndDate', 'Registration End Date', '', form.tournamentStartDate, 'tournamentBanner'],
              ].map(([key, label, min, max, next]) => <div className="col-md-6" key={key}><label className="form-label" htmlFor={`tournament-${key}`}>{label}</label><input id={`tournament-${key}`} ref={fieldRefs[key]} type="date" min={min || undefined} max={max || undefined} aria-invalid={Boolean(errors[key])} aria-describedby={`tournament-${key}-error`} className={`form-control ${errors[key] ? 'is-invalid' : ''}`} value={form[key]} onChange={(e) => { setField(key, e.target.value); window.setTimeout(() => moveTo(next), 100); }} /><FieldError id={`tournament-${key}-error`} message={errors[key]} /></div>)}
            </div>
          </div>

          <div className="card p-3 d-grid gap-3">
            <div><h2 className="h6">Ball Type *</h2><ChipGroup options={BALL_OPTIONS} value={form.ballType} onChange={(value) => setForm((p) => ({ ...p, ballType: value }))} /></div>
            <div><h2 className="h6">Pitch Type</h2><ChipGroup options={PITCH_OPTIONS} value={form.pitchType} onChange={(value) => setForm((p) => ({ ...p, pitchType: value }))} /></div>
            <div><h2 className="h6">Match Type *</h2><ChipGroup options={MATCH_OPTIONS} value={form.matchType} onChange={(value) => setForm((p) => ({ ...p, matchType: value }))} /></div>
            <div className="row g-3">
              <div className="col-md-6"><label className="form-label">Max Players Allowed</label><input type="number" min="1" className="form-control" value={form.maxPlayersAllowed} onChange={(e) => setForm((p) => ({ ...p, maxPlayersAllowed: e.target.value }))} /></div>
            </div>
          </div>

          <div className="card p-3">
            <h2 className="h6">Media</h2>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Tournament Banner</label>
                <input ref={fieldRefs.tournamentBanner} aria-invalid={Boolean(errors.tournamentBanner)} aria-describedby="tournament-banner-error" type="file" accept="image/*" className={`form-control ${errors.tournamentBanner ? 'is-invalid' : ''}`} onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setField('tournamentBanner', file || '');
                  setBannerPreview(file ? URL.createObjectURL(file) : '');
                  window.setTimeout(() => moveTo('tournamentLogo'), 100);
                }} />
                <FieldError id="tournament-banner-error" message={errors.tournamentBanner} />
                {bannerPreview ? <img src={bannerPreview} alt="Tournament banner preview" className="img-fluid rounded mt-2" style={{ height: 120, width: '100%', objectFit: 'cover' }} /> : null}
              </div>
              <div className="col-md-6">
                <label className="form-label">Tournament Logo</label>
                <input ref={fieldRefs.tournamentLogo} aria-invalid={Boolean(errors.tournamentLogo)} aria-describedby="tournament-logo-error" type="file" accept="image/*" className={`form-control ${errors.tournamentLogo ? 'is-invalid' : ''}`} onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setField('tournamentLogo', file || '');
                  setLogoPreview(file ? URL.createObjectURL(file) : '');
                }} />
                <FieldError id="tournament-logo-error" message={errors.tournamentLogo} />
                {logoPreview ? <img src={logoPreview} alt="Tournament logo preview" className="mt-2" style={{ height: 84, width: 84, borderRadius: '50%', objectFit: 'cover' }} /> : null}
              </div>
            </div>
          </div>

          <button disabled={submitting} className="btn btn-primary btn-lg" type="submit">{submitting ? 'Creating...' : 'Register Tournament'}</button>
          {status ? <div className="alert alert-info mb-0">{status}</div> : null}
        </form>
      </div>
    </PageShell>
  );
};

export default IndividualTournamentCreatePage;
